import React, { createContext, useState, useContext, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { getSubscriptionStatus } from '../api/subscriptionApi';
import { UserSubscription } from '../models/userSubscription';

interface SubscriptionContextType {
    subscription: UserSubscription | null;
    isLoading: boolean;
    error: string | null;
    refreshSubscription: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { isAuthenticated, isLoading: isAuthLoading } = useAuth0();
    const [subscription, setSubscription] = useState<UserSubscription | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const loadSubscription = async () => {
        if (!isAuthenticated) {
            setSubscription(null);
            return;
        }

        try {
            setIsLoading(true);
            setError(null);
            const data = await getSubscriptionStatus();
            setSubscription(data);
        } catch (err) {
            console.error('Error fetching subscription status:', err);
            setError('Failed to load subscription information');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        // Only load subscription when auth is done loading and user is authenticated
        if (!isAuthLoading && isAuthenticated) {
            loadSubscription();
        }
    }, [isAuthLoading, isAuthenticated]);

    const refreshSubscription = async () => {
        await loadSubscription();
    };

    return (
        <SubscriptionContext.Provider 
            value={{ 
                subscription, 
                isLoading: isAuthLoading || isLoading, 
                error, 
                refreshSubscription 
            }}
        >
            {children}
        </SubscriptionContext.Provider>
    );
};

export const useSubscription = () => {
    const context = useContext(SubscriptionContext);
    if (context === undefined) {
        throw new Error('useSubscription must be used within a SubscriptionProvider');
    }
    return context;
}; 